import {
  Button,
  Card,
  Checkbox,
  Chip,
  Description,
  Drawer,
  IconChevronUp,
  Input,
} from "@heroui/react"
import type { SelfOrderLoyaltyIdentity, SelfOrderMenu, SelfOrderSession } from "@ramoo/contracts"
import { tamu } from "@ramoo/i18n/tamu"
import { bisaDipesan, rupiah } from "@ramoo/self-order-ui"
import { useEffect, useMemo, useRef, useState } from "react"
import { ambilMenu, ambilSesi, kirimOtpLoyalty, kirimPesanan, verifikasiOtpLoyalty } from "./api"
import { Bayar } from "./Bayar"
import { JudulPusat, Jumlah, Memuat, Pusat } from "./bagian"
import { DetailProduk } from "./DetailProduk"
import { KakiEtalase, Sampul, Spanduk, TierLoyalty, useWarnaMerek } from "./Etalase"
import { KatalogTamu } from "./KatalogTamu"
import {
  grupProduk,
  hargaSatuan,
  kunciItem,
  type PilihanItem,
  type Produk,
  punyaOpsi,
} from "./katalog"
import { Lacak } from "./Lacak"

const kunciTerakhir = (outletId: string) => `ramoo-so-last:${outletId}`
const teks = tamu()

type ItemKeranjang = {
  key: string
  productId: string
  variantId: string | null
  nama: string
  /** Ringkasan varian + modifier untuk ditampilkan di keranjang (mis. "Besar · Extra shot"). */
  ringkas: string | null
  modifierIds: string[]
  notes: string | null
  harga: number
  jumlah: number
  /** Batas jumlah per pesanan (`maxPerOrder`); null = tak terbatas. */
  maks: number | null
}

/** URL QR: `order.ramoo.id/{outletId}/{token}`. Scope datang dari sini, dipegang pelanggan. */
function paramDariUrl(): { outletId: string; token: string } | null {
  const bagian = window.location.pathname.split("/").filter(Boolean)
  const [outletId, token] = bagian
  return outletId && token ? { outletId, token } : null
}

export function App() {
  const param = useMemo(paramDariUrl, [])
  const [sesi, setSesi] = useState<SelfOrderSession | null>(null)
  const [menu, setMenu] = useState<SelfOrderMenu | null>(null)
  const [galat, setGalat] = useState<string | null>(null)
  const [keranjang, setKeranjang] = useState<ItemKeranjang[]>([])
  /* Lembar detail. `sesi` naik tiap kali dibuka dan jadi `key`-nya, supaya pilihan selalu mulai
     kosong; produknya tetap dipegang sesudah ditutup supaya lembar sempat beranimasi keluar. */
  const [detail, setDetail] = useState<{ produk: Produk; sesi: number } | null>(null)
  const [detailBuka, setDetailBuka] = useState(false)
  const [kirim, setKirim] = useState(false)
  const [sukses, setSukses] = useState<{
    orderId: string
    receiptNumber: string
    pointsRedeemed: number
    loyaltyDiscount: number
  } | null>(null)
  const [bayar, setBayar] = useState(false)
  /** Lembar Pesanan (keranjang) terbuka; hanya lewat bar bawah, tidak pernah terbuka sendiri. */
  const [lembar, setLembar] = useState(false)
  /* Turnstile: hanya aktif kalau site key diisi saat build (prod). Server memverifikasi tokennya
     kalau TURNSTILE_SECRET aktif — dev tak butuh Cloudflare. */
  const sitekey = import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined
  const [turnstileToken, setTurnstileToken] = useState("")
  /* Kumpul poin (opsional): nama + no HP menautkan pesanan ke pelanggan. Poin baru berarti kalau
     toko mengaktifkan loyalty; kalau tidak, ini tak berefek. */
  const [nama, setNama] = useState("")
  const [hp, setHp] = useState("")
  /* L6 Modul 12: consent promo WhatsApp, bawaan TIDAK dicentang. Server mencatatnya hanya untuk
     pelanggan yang lahir dari pesanan ini; tidak dicentang = tidak menyatakan apa-apa (null). */
  const [setujuPromo, setSetujuPromo] = useState(false)
  const [otpTerkirim, setOtpTerkirim] = useState(false)
  const [kodeOtp, setKodeOtp] = useState("")
  const [identitas, setIdentitas] = useState<SelfOrderLoyaltyIdentity | null>(null)
  /* Nama depan pelanggan yang dipilih kasir saat membuka meja (QR order). Ada = pesanan sudah
     tercatat atas namanya, jadi nama dan HP tidak diminta lagi; penukaran poin ikut tidak
     ditawarkan di sini (tetap butuh OTP, dan masih ditunda). */
  const pelangganKasir = sesi?.customer?.firstName ?? null
  const [poinDitukar, setPoinDitukar] = useState(0)
  const [sibukOtp, setSibukOtp] = useState(false)
  /* Pesanan terakhir (di perangkat ini) untuk "pesan lagi" — kenyamanan per-peramban, bukan data
     server. Dibungkus try/catch: localStorage bisa gagal di mode privat. */
  const [terakhir, setTerakhir] = useState<ItemKeranjang[] | null>(null)

  /* Judul tab = nama toko yang dibuka tamu, sama dengan kepala halamannya. */
  const namaToko = sesi ? (sesi.storefront.displayName ?? sesi.outlet.name) : null
  useEffect(() => {
    if (namaToko) document.title = teks.judulTab(namaToko)
  }, [namaToko])
  useEffect(() => {
    if (!param) {
      setGalat("QR meja tidak lengkap. Pindai ulang QR di meja.")
      return
    }
    Promise.all([ambilSesi(param.outletId, param.token), ambilMenu(param.outletId, param.token)])
      .then(([s, m]) => {
        setSesi(s)
        setMenu(m)
      })
      .catch((e: Error) => setGalat(e.message))
    try {
      const raw = localStorage.getItem(kunciTerakhir(param.outletId))
      if (raw) setTerakhir(JSON.parse(raw) as ItemKeranjang[])
    } catch {
      /* localStorage tak tersedia (mode privat) — reorder cuma tak muncul, bukan galat. */
    }
  }, [param])

  useWarnaMerek(sesi?.storefront)

  const total = keranjang.reduce((a, i) => a + i.harga * i.jumlah, 0)
  /* Jumlah per produk (semua varian dijumlah) untuk angka di tombol kartu katalog. */
  const jumlahDi = useMemo(() => {
    const m = new Map<string, number>()
    for (const i of keranjang) m.set(i.productId, (m.get(i.productId) ?? 0) + i.jumlah)
    return m
  }, [keranjang])
  /* Keranjang dikosongkan dari lembar: lembarnya ikut tertutup, supaya tidak terbuka lagi sendiri
     saat produk berikutnya ditambahkan. */
  useEffect(() => {
    if (!keranjang.length) setLembar(false)
  }, [keranjang.length])
  const maksPoin = identitas
    ? Math.floor(
        Math.min(
          identitas.customer.points,
          Math.floor((total * identitas.maxRedeemPercent) / 100) *
            identitas.pointsPerRupiahDiscount,
        ) / identitas.pointsPerRupiahDiscount,
      ) * identitas.pointsPerRupiahDiscount
    : 0

  /** Produk sederhana quick-add; produk dengan varian/modifier membuka sheet (tak pernah otomatis
   *  memilih varian pertama). */
  function pilihProduk(p: Produk) {
    /* Kartu habis tak punya tombol tambah; penjaga ini untuk jalur lain (ADR-0017). */
    if (!bisaDipesan(p)) return
    if (punyaOpsi(p)) {
      setDetail((d) => ({ produk: p, sesi: (d?.sesi ?? 0) + 1 }))
      setDetailBuka(true)
      return
    }
    tambahKeKeranjang(p, { variantId: null, modifierIds: [], jumlah: 1, notes: null })
  }

  function tambahKeKeranjang(p: Produk, pilihan: PilihanItem) {
    if (!bisaDipesan(p)) return
    const grup = grupProduk(p, menu?.modifierGroups ?? [])
    const satuan = hargaSatuan(p, grup, pilihan)
    const key = kunciItem(p.id, pilihan)
    const variant = pilihan.variantId ? p.variants.find((v) => v.id === pilihan.variantId) : null
    const modifierNama = grup
      .flatMap((g) => g.modifiers)
      .filter((m) => pilihan.modifierIds.includes(m.id))
      .map((m) => m.name)
    const ringkas = [variant?.name, ...modifierNama].filter(Boolean).join(" · ") || null
    setKeranjang((k) => {
      const ada = k.find((x) => x.key === key)
      if (ada)
        return k.map((x) =>
          x.key === key
            ? {
                ...x,
                jumlah: Math.min(x.maks ?? Number.POSITIVE_INFINITY, x.jumlah + pilihan.jumlah),
              }
            : x,
        )
      return [
        ...k,
        {
          key,
          productId: p.id,
          variantId: pilihan.variantId,
          nama: p.name,
          ringkas,
          modifierIds: pilihan.modifierIds,
          notes: pilihan.notes,
          harga: satuan,
          jumlah: pilihan.jumlah,
          maks: p.maxPerOrder,
        },
      ]
    })
  }

  function ubahJumlah(key: string, delta: number) {
    setKeranjang((k) =>
      k
        .map((x) =>
          x.key === key
            ? { ...x, jumlah: Math.min(x.maks ?? Number.POSITIVE_INFINITY, x.jumlah + delta) }
            : x,
        )
        .filter((x) => x.jumlah > 0),
    )
  }

  async function pesan() {
    if (!param || !keranjang.length) return
    setKirim(true)
    setGalat(null)
    try {
      const res = await kirimPesanan(param.outletId, param.token, {
        /* Pelanggan pilihan kasir (QR order) diwarisi server dari tagihan mejanya; halaman ini tidak
           mengirim nama atau HP apa pun untuknya. */
        customerName: pelangganKasir ? null : nama.trim() || null,
        customerPhone: pelangganKasir ? null : hp.trim() || null,
        marketingConsent: !pelangganKasir && hp.trim() && setujuPromo ? true : null,
        notes: null,
        turnstileToken: turnstileToken || null,
        redeemPoints: poinDitukar,
        loyaltyProofToken: poinDitukar > 0 ? (identitas?.proofToken ?? null) : null,
        lines: keranjang.map((i) => ({
          productId: i.productId,
          variantId: i.variantId,
          quantity: i.jumlah,
          modifierIds: i.modifierIds,
          notes: i.notes,
        })),
      })
      try {
        localStorage.setItem(kunciTerakhir(param.outletId), JSON.stringify(keranjang))
      } catch {
        /* abaikan — reorder cuma kenyamanan */
      }
      setTerakhir(keranjang)
      setSukses({
        orderId: res.orderId,
        receiptNumber: res.receiptNumber,
        pointsRedeemed: res.pointsRedeemed,
        loyaltyDiscount: res.loyaltyDiscount,
      })
      setKeranjang([])
      setLembar(false)
      setIdentitas(null)
      setPoinDitukar(0)
    } catch (e) {
      setGalat((e as Error).message)
      /* Menu bisa berubah selagi tamu memilih: kasir menandai habis (ADR-0017) dan server
         menolak pesanannya. Muat ulang menunya supaya kartu itu ikut abu-abu "Habis". */
      ambilMenu(param.outletId, param.token)
        .then(setMenu)
        .catch(() => {})
    } finally {
      setKirim(false)
    }
  }

  async function mintaOtp() {
    if (!param || !hp.trim()) return
    setSibukOtp(true)
    setGalat(null)
    try {
      await kirimOtpLoyalty(param.outletId, param.token, hp)
      setOtpTerkirim(true)
      setKodeOtp("")
      setIdentitas(null)
      setPoinDitukar(0)
    } catch (e) {
      setGalat((e as Error).message)
    } finally {
      setSibukOtp(false)
    }
  }

  async function periksaOtp() {
    if (!param || kodeOtp.length !== 6) return
    setSibukOtp(true)
    setGalat(null)
    try {
      const hasil = await verifikasiOtpLoyalty(param.outletId, param.token, hp, kodeOtp)
      setIdentitas(hasil)
      setNama((n) => n.trim() || hasil.customer.name)
      setOtpTerkirim(false)
    } catch (e) {
      setGalat((e as Error).message)
    } finally {
      setSibukOtp(false)
    }
  }

  /** Pesan lagi seperti terakhir (di perangkat ini): isi ulang keranjang dari item yang masih di menu.
   *  Harga tetap dihitung ulang server saat memesan — ini cuma pintasan pemilihan. */
  function ulangi() {
    if (!terakhir || !menu) return
    const tersedia = terakhir.filter((i) =>
      menu.products.some((p) => p.id === i.productId && bisaDipesan(p)),
    )
    if (tersedia.length) setKeranjang(tersedia)
  }

  if (galat && !menu)
    return (
      <Pusat>
        <JudulPusat>{teks.ups}</JudulPusat>
        <p className="text-sm text-muted">{galat}</p>
      </Pusat>
    )
  if (sukses && bayar && param)
    return (
      <Bayar
        outletId={param.outletId}
        token={param.token}
        orderId={sukses.orderId}
        /* Lunas atau "bayar di kasir saja": kembali ke halaman pesanan, bukan ke katalog — di
           sanalah status pembayaran dan isi pesanannya terbaca. */
        onSelesai={() => setBayar(false)}
      />
    )
  if (!sesi || !menu)
    return (
      <Pusat>
        <Memuat>{teks.memuat}</Memuat>
      </Pusat>
    )

  const namaTampil = sesi.storefront.displayName ?? sesi.outlet.name
  const labelMeja = pelangganKasir
    ? teks.mejaAtasNama(sesi.table.label, pelangganKasir)
    : teks.meja(sesi.table.label)
  const kepala = <KepalaTamu nama={namaTampil} meja={labelMeja} />

  /* Terkirim dan Lacak jadi satu halaman (mockup Modul 12): kartu bayar (status, total, Bayar
     QRIS) di atas, tahap tiap baris di bawahnya. Dulu dua layar, dan Lacak "Menunggu pembayaran"
     tidak punya tombol bayar maupun total. */
  if (sukses && param)
    return (
      <Lacak
        outletId={param.outletId}
        token={param.token}
        orderId={sukses.orderId}
        receiptNumber={sukses.receiptNumber}
        kepala={kepala}
        poin={
          sukses.pointsRedeemed > 0
            ? teks.poinDipakai(sukses.pointsRedeemed, rupiah(sukses.loyaltyDiscount))
            : null
        }
        onBayar={() => setBayar(true)}
        onPesanLagi={() => setSukses(null)}
      />
    )

  const jumlahItem = keranjang.reduce((a, i) => a + i.jumlah, 0)

  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col pb-24">
      {kepala}
      <Sampul storefront={sesi.storefront} namaOutlet={sesi.outlet.name} />
      <Spanduk storefront={sesi.storefront} />
      <TierLoyalty storefront={sesi.storefront} />
      <main className="flex flex-col gap-3 px-4 py-3">
        {!keranjang.length && terakhir?.length ? (
          <Button fullWidth variant="secondary" onPress={ulangi}>
            {teks.ulangiTerakhir}
          </Button>
        ) : null}
        <KatalogTamu
          menu={menu}
          tampilkanKategori={sesi.storefront.showCategories}
          onPilih={pilihProduk}
          jumlahDi={jumlahDi}
        />
        {galat && !lembar ? <Galat>{galat}</Galat> : null}
        <KakiEtalase storefront={sesi.storefront} outletId={param?.outletId ?? ""} />
      </main>
      {/* Bar ringkas "3 item · Rp70.000" (mockup Modul 12) yang membuka lembar Pesanan. Dulu
          panel keranjang terbuka sendiri dan tumbuh per item sampai sepertiga layar. */}
      {keranjang.length ? (
        <div className="fixed inset-x-0 bottom-0 z-10 mx-auto max-w-xl px-4 pt-2.5 pb-[calc(--spacing(4)+env(safe-area-inset-bottom))]">
          <Button
            fullWidth
            size="lg"
            className="justify-between shadow-lg"
            aria-label={teks.lihatPesanan(jumlahItem, rupiah(total))}
            onPress={() => setLembar(true)}
          >
            <span className="rounded-full bg-accent-foreground/15 px-2.5 py-0.5 text-sm tabular-nums">
              {teks.barItem(jumlahItem)}
            </span>
            <span className="flex items-center gap-1 tabular-nums">
              {rupiah(total)}
              <IconChevronUp aria-hidden="true" className="size-4" />
            </span>
          </Button>
        </div>
      ) : null}
      <Drawer.Backdrop
        isOpen={lembar && keranjang.length > 0}
        onOpenChange={(terbuka) => {
          if (!terbuka) setLembar(false)
        }}
      >
        <Drawer.Content placement="bottom">
          <Drawer.Dialog aria-label={teks.pesanan} className="mx-auto max-w-xl">
            <Drawer.Handle />
            <Drawer.CloseTrigger aria-label={teks.tutup} />
            <Drawer.Header className="flex-row items-baseline gap-2">
              <Drawer.Heading>{teks.pesanan}</Drawer.Heading>
              <span className="text-sm text-muted">{labelMeja}</span>
            </Drawer.Header>
            <Drawer.Body className="flex flex-col gap-3">
              <div className="flex flex-col">
                {keranjang.map((i) => (
                  <div
                    key={i.key}
                    className="flex min-h-14 items-center gap-2 border-b border-separator py-2 last:border-b-0"
                  >
                    <div className="flex min-w-0 flex-1 flex-col text-sm">
                      <span>{i.nama}</span>
                      {i.ringkas ? <span className="text-xs text-muted">{i.ringkas}</span> : null}
                      {i.notes ? <span className="text-xs text-muted">“{i.notes}”</span> : null}
                    </div>
                    <Jumlah
                      nilai={i.jumlah}
                      bisaTambah={i.maks == null || i.jumlah < i.maks}
                      onKurang={() => ubahJumlah(i.key, -1)}
                      onTambah={() => ubahJumlah(i.key, 1)}
                    />
                    <span className="min-w-20 text-end text-sm font-semibold tabular-nums">
                      {rupiah(i.harga * i.jumlah)}
                    </span>
                  </div>
                ))}
              </div>
              {pelangganKasir ? null : (
                <div className="flex flex-col gap-2">
                  <Input
                    fullWidth
                    value={nama}
                    onChange={(e) => setNama(e.target.value)}
                    placeholder={teks.namaOpsional}
                    aria-label={teks.namaOpsional}
                    maxLength={80}
                  />
                  <Input
                    fullWidth
                    value={hp}
                    onChange={(e) => {
                      setHp(e.target.value)
                      setOtpTerkirim(false)
                      setKodeOtp("")
                      setIdentitas(null)
                      setPoinDitukar(0)
                    }}
                    placeholder={teks.hpOpsional}
                    aria-label={teks.hpOpsional}
                    inputMode="tel"
                    maxLength={30}
                  />
                  {hp.trim() && !nama.trim() ? (
                    <p className="text-center text-xs text-muted">{teks.namaUntukPoin}</p>
                  ) : null}
                  {hp.trim() ? (
                    <Checkbox isSelected={setujuPromo} onChange={setSetujuPromo}>
                      <Checkbox.Content>
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        {teks.consentPromo}
                      </Checkbox.Content>
                      <Description>{teks.consentPromoPetunjuk}</Description>
                    </Checkbox>
                  ) : null}
                  {sesi.storefront.loyalty?.redemptionEnabled && hp.trim() ? (
                    identitas ? (
                      <Card variant="secondary" className="gap-2 p-3">
                        <Card.Title>Nomor terverifikasi</Card.Title>
                        <Card.Description className="text-xs">
                          Saldo {identitas.customer.points} poin · maksimal {maksPoin} poin untuk
                          pesanan ini
                        </Card.Description>
                        {maksPoin >= identitas.minRedeemPoints ? (
                          <Input
                            fullWidth
                            type="number"
                            inputMode="numeric"
                            min={identitas.minRedeemPoints}
                            max={maksPoin}
                            step={identitas.pointsPerRupiahDiscount}
                            value={poinDitukar || ""}
                            placeholder={`Poin yang dipakai (min. ${identitas.minRedeemPoints})`}
                            aria-label={`Poin yang dipakai (min. ${identitas.minRedeemPoints})`}
                            onChange={(e) =>
                              setPoinDitukar(
                                Math.min(maksPoin, Math.max(0, Number(e.target.value) || 0)),
                              )
                            }
                          />
                        ) : (
                          <Card.Description className="text-xs">
                            Belum mencapai minimum {identitas.minRedeemPoints} poin untuk ditukar.
                          </Card.Description>
                        )}
                      </Card>
                    ) : otpTerkirim ? (
                      <div className="flex gap-2">
                        <Input
                          fullWidth
                          value={kodeOtp}
                          onChange={(e) =>
                            setKodeOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                          }
                          placeholder="6 digit kode OTP"
                          aria-label="6 digit kode OTP"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                        />
                        <Button
                          className="shrink-0"
                          isDisabled={sibukOtp || kodeOtp.length !== 6}
                          onPress={() => void periksaOtp()}
                        >
                          Verifikasi
                        </Button>
                      </div>
                    ) : (
                      <Button
                        fullWidth
                        variant="outline"
                        isDisabled={sibukOtp}
                        onPress={() => void mintaOtp()}
                      >
                        {sibukOtp ? "Mengirim kode…" : "Verifikasi nomor untuk tukar poin"}
                      </Button>
                    )
                  ) : null}
                </div>
              )}
              {sitekey ? <Turnstile sitekey={sitekey} onToken={setTurnstileToken} /> : null}
            </Drawer.Body>
            <Drawer.Footer className="flex-col items-stretch gap-2">
              {galat ? <Galat>{galat}</Galat> : null}
              <Button
                fullWidth
                size="lg"
                isDisabled={
                  kirim ||
                  (!!sitekey && !turnstileToken) ||
                  (!pelangganKasir && !!hp.trim() && !nama.trim())
                }
                onPress={() => void pesan()}
              >
                {kirim ? teks.mengirim : teks.pesan(rupiah(total))}
              </Button>
              <p className="text-center text-xs text-muted">{teks.catatanHarga}</p>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
      {detail ? (
        <DetailProduk
          key={detail.sesi}
          produk={detail.produk}
          semuaGrup={menu.modifierGroups}
          buka={detailBuka}
          onTutup={() => setDetailBuka(false)}
          onTambah={(pilihan) => tambahKeKeranjang(detail.produk, pilihan)}
        />
      ) : null}
    </div>
  )
}

/**
 * Kepala halaman tamu: nama toko satu baris dan pil meja (mockup Modul 12). Dulu dua baris besar
 * — nama 18 px lalu "Meja A1" di bawahnya.
 */
function KepalaTamu({ nama, meja }: { nama: string; meja: string }) {
  return (
    <header className="sticky top-0 z-10 flex h-13 items-center gap-3 border-b border-separator bg-surface px-4">
      <p className="min-w-0 flex-1 truncate text-base font-semibold">{nama}</p>
      <Chip color="accent" variant="soft" size="sm" className="shrink-0">
        {meja}
      </Chip>
    </header>
  )
}

/** Pesan galat di tempat isi itu seharusnya. */
function Galat({ children }: { children: string }) {
  return (
    <p className="p-3 text-center text-sm text-danger-soft-foreground" role="alert">
      {children}
    </p>
  )
}

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: { sitekey: string; callback: (t: string) => void; "error-callback"?: () => void },
  ) => void
}

/**
 * Widget Turnstile — hanya dirender kalau `VITE_TURNSTILE_SITEKEY` diisi (prod). Tokennya dikirim ke
 * server bersama pesanan; server memverifikasi kalau `TURNSTILE_SECRET` aktif.
 */
export function Turnstile({ sitekey, onToken }: { sitekey: string; onToken: (t: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const idScript = "cf-turnstile-script"
    const pasang = () => {
      const ts = (window as unknown as { turnstile?: TurnstileApi }).turnstile
      if (ts && ref.current && !ref.current.hasChildNodes())
        ts.render(ref.current, { sitekey, callback: onToken, "error-callback": () => onToken("") })
    }
    if (document.getElementById(idScript)) {
      pasang()
      return
    }
    const s = document.createElement("script")
    s.id = idScript
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
    s.async = true
    s.onload = pasang
    document.head.appendChild(s)
  }, [sitekey, onToken])
  return <div ref={ref} className="flex justify-center" />
}
